import { ComponentType, createContext, useContext } from 'react';
import {
  SidePanelPropertyDescriptor,
  SidePanelPropertyKey,
  SidePanelRowProps,
} from 'src/graphql/entities/side-panel-personalization';
import { CUSTOM_ATTRIBUTE_TYPE_ICONS } from './custom-attributes.mappings';
import {
  CustomAttributeValue,
  NormalizedCustomAttributeDefinition,
} from './custom-attributes.types';
import { CustomAttributeRowData } from './field-view-registry/types';

export interface CustomAttributesSidePanelData {
  rowsByPropertyKey: Map<SidePanelPropertyKey, CustomAttributeRowData>;
  /** the entity's partner — related cards load their definitions for it too */
  partnerId: number | undefined;
  loading: boolean;
  payloadError: string | null;
  disabled: boolean;
  onSave: (
    definition: NormalizedCustomAttributeDefinition,
    value: CustomAttributeValue,
  ) => void;
}

export const CustomAttributesSidePanelContext =
  createContext<CustomAttributesSidePanelData | null>(null);

export function useCustomAttributesSidePanel(): CustomAttributesSidePanelData {
  const value = useContext(CustomAttributesSidePanelContext);

  if (!value) {
    throw new Error(
      'useCustomAttributesSidePanel must be used within CustomAttributesSidePanelProvider',
    );
  }

  return value;
}

export function buildCustomAttributeSidePanelDescriptors(
  rowsByPropertyKey: Map<SidePanelPropertyKey, CustomAttributeRowData>,
  Row: ComponentType<SidePanelRowProps>,
): SidePanelPropertyDescriptor[] {
  return [...rowsByPropertyKey.values()].map((row) => ({
    ...toSidePanelDescriptor(row.definition),
    Row,
    isEmpty: row.isEmpty,
  }));
}

/** Without `Row` the descriptor is card-rendered: the entity card shows the value itself. */
export function toSidePanelDescriptor(
  definition: NormalizedCustomAttributeDefinition,
): SidePanelPropertyDescriptor {
  return {
    key: `ca:${definition.key}`,
    label: definition.label,
    group: definition.groupKey
      ? { key: definition.groupKey, label: definition.groupLabel ?? definition.groupKey }
      : undefined,
    defaultVisible: false,
    icon: CUSTOM_ATTRIBUTE_TYPE_ICONS[definition.type],
  };
}
